// File: frontend/src/components/admin/charts/index.js
//
// SVG charts driven by the --color-chart-* tokens. The originals hardcoded
// #22c55e, #3b82f6, #667eea and a #f1f5f9 donut track, so every chart kept its
// light-mode colours on the dark theme and the donut's unfilled ring glowed
// near-white on charcoal.
//
// SVG `fill`/`stroke` take a CSS variable directly, so the values come from
// the same tokens the rest of the UI uses and follow the theme for free.
import { formatCalendarDate } from '../../../utils/adminDates';
import { EmptyState } from '../primitives';

export const CHART_TOKENS = [
  'var(--color-chart-1)', 'var(--color-chart-2)', 'var(--color-chart-3)',
  'var(--color-chart-4)', 'var(--color-chart-5)',
];
const TRACK = 'var(--color-chart-track)';

// Day-of-month from a 'YYYY-MM-DD' bucket, read off the string rather than
// through Date() — parsing it yields UTC midnight, which prints the previous
// day west of Greenwich and made the axis disagree with the data.
const dayNumber = (value) => {
  const m = /^\d{4}-\d{2}-(\d{2})/.exec(String(value));
  return m ? String(Number(m[1])) : '';
};

export function BarChart({ data, token = CHART_TOKENS[0], height = 120, label }) {
  if (!data || data.length === 0) return <EmptyState>No data yet</EmptyState>;
  const max = Math.max(...data.map(d => Number(d.count) || 0), 1);
  const barWidth = 100 / data.length;
  // Only label every nth bar on a 30-day series; they overlap into a grey
  // smear otherwise.
  const labelEvery = Math.ceil(data.length / 10);

  return (
    <figure className="m-0" aria-label={label || `Bar chart, ${data.length} points, peak ${max}`}>
      {/* preserveAspectRatio="none" so the bars stretch to fill the card.
          Without it the SVG keeps its 100-wide viewBox in proportion and
          letterboxes itself into a narrow strip in the middle of a wide
          container — which is what the original chart did.
          Axis labels are HTML underneath rather than <text> inside, because
          "none" would stretch glyphs horizontally along with everything else. */}
      <svg
        viewBox={`0 0 100 ${height}`}
        preserveAspectRatio="none"
        className="w-full block"
        style={{ height }}
        role="img"
      >
        {data.map((d, i) => {
          const count = Number(d.count) || 0;
          const barHeight = (count / max) * height;
          return (
            <rect
              key={d.date || i}
              x={i * barWidth + barWidth * 0.15}
              y={height - barHeight}
              width={barWidth * 0.7}
              height={Math.max(barHeight, count > 0 ? 1 : 0)}
              fill={token}
            >
              <title>{`${formatCalendarDate(d.date) || d.date}: ${count}`}</title>
            </rect>
          );
        })}
      </svg>
      <div className="flex w-full mt-1" aria-hidden="true">
        {data.map((d, i) => (
          <span
            key={d.date || i}
            className="text-[10px] text-ink-subtle text-center overflow-hidden"
            style={{ width: `${barWidth}%` }}
          >
            {i % labelEvery === 0 ? dayNumber(d.date) : ''}
          </span>
        ))}
      </div>
      <figcaption className="sr-only">
        {label || `Bar chart, ${data.length} points, peak ${max}`}
      </figcaption>
    </figure>
  );
}

// A compact trend line with no axes, for a single row in a list.
export function Sparkline({ data, height = 28, token = CHART_TOKENS[0], label }) {
  if (!data || data.length === 0) return <span className="text-xs text-ink-subtle">No activity</span>;
  const max = Math.max(...data.map(d => Number(d.count) || 0), 1);
  const step = data.length > 1 ? 100 / (data.length - 1) : 100;
  const points = data
    .map((d, i) => `${(i * step).toFixed(2)},${(height - (Number(d.count) / max) * (height - 2) - 1).toFixed(2)}`)
    .join(' ');

  return (
    <svg
      viewBox={`0 0 100 ${height}`}
      preserveAspectRatio="none"
      className="w-full"
      style={{ height }}
      role="img"
      aria-label={label || `Trend over ${data.length} days, peak ${max}`}
    >
      <polyline points={points} fill="none" stroke={token} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

// `slices` is [{ label, value }]. Zero-value slices are dropped rather than
// drawn as an invisible arc that still consumes a legend row and a colour.
export function DonutChart({ slices, size = 120, centerLabel }) {
  const present = (slices || []).filter(s => Number(s.value) > 0);
  const total = present.reduce((n, s) => n + Number(s.value), 0);
  if (total === 0) return <EmptyState>No data yet</EmptyState>;

  let offset = 0;
  return (
    <div className="flex flex-wrap items-center gap-4">
      <svg width={size} height={size} viewBox="0 0 42 42" role="img" aria-label={`Breakdown of ${total}`}>
        <circle cx="21" cy="21" r="15.91549431" fill="transparent" stroke={TRACK} strokeWidth="6" />
        {present.map((s, i) => {
          const percent = (Number(s.value) / total) * 100;
          const arc = `${percent} ${100 - percent}`;
          const dashOffset = 25 - offset;
          offset += percent;
          return (
            <circle
              key={s.label}
              cx="21" cy="21" r="15.91549431"
              fill="transparent"
              stroke={CHART_TOKENS[i % CHART_TOKENS.length]}
              strokeWidth="6"
              strokeDasharray={arc}
              strokeDashoffset={dashOffset}
            >
              <title>{`${s.label}: ${s.value}`}</title>
            </circle>
          );
        })}
        <text
          x="21" y="21" textAnchor="middle" dy="0.35em"
          fill="var(--color-ink-main)"
          style={{ fontSize: '8px', fontWeight: 700 }}
        >
          {centerLabel ?? total}
        </text>
      </svg>
      <ul className="space-y-1 min-w-0">
        {present.map((s, i) => (
          <li key={s.label} className="flex items-center gap-2 text-xs">
            <span
              className="w-3 h-3 rounded-full shrink-0"
              style={{ backgroundColor: CHART_TOKENS[i % CHART_TOKENS.length] }}
            />
            <span className="text-ink-light truncate">{s.label}</span>
            <span className="admin-nums font-semibold text-ink-main">{s.value}</span>
            <span className="text-ink-subtle">({Math.round((s.value / total) * 100)}%)</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Ranked horizontal bars — "top habits", "users by platform". The label sits
// above the bar on a phone rather than in a fixed-width column beside it,
// which is what used to squeeze the bar down to nothing at 375px.
export function RankedBars({ rows, token = CHART_TOKENS[0], emptyLabel = 'No data yet' }) {
  if (!rows || rows.length === 0) return <EmptyState>{emptyLabel}</EmptyState>;
  const max = Math.max(...rows.map(r => Number(r.value) || 0), 1);

  return (
    <ul className="space-y-2.5">
      {rows.map((row, i) => (
        <li key={row.label || i}>
          <div className="flex items-baseline justify-between gap-2 mb-1">
            <span className="text-sm text-ink-main truncate capitalize">{row.label || 'Unknown'}</span>
            <span className="admin-nums text-sm text-ink-light shrink-0">
              {Number(row.value).toLocaleString()}
              {row.suffix && <span className="text-ink-subtle"> {row.suffix}</span>}
            </span>
          </div>
          <div className="h-2 bg-surface-input rounded-full overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{ width: `${Math.max((Number(row.value) / max) * 100, 2)}%`, backgroundColor: token }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}
